import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useSession } from '@/lib/auth-client';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  RiTeamLine,
  RiUserAddLine,
  RiShieldUserLine,
  RiMailSendLine,
  RiSearchLine,
  RiMoreLine,
  RiDeleteBinLine,
  RiEditLine,
  RiCheckLine,
  RiCloseLine,
  RiLoader2Line,
} from '@remixicon/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

interface TeamMember {
  id: string;
  userId: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joinedAt: string;
  userName: string;
  userEmail: string;
  userImage?: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  memberCount?: number;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'expired';
  expiresAt: string;
  createdAt: string;
  invitedBy: string;
}

export function TeamPage() {
  const { data: session } = useSession();
  const [searchQuery, setSearchQuery] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [organizationId, setOrganizationId] = useState<string>('');
  const [isLoadingOrganization, setIsLoadingOrganization] = useState(true);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState<{ id: string; email: string } | null>(null);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: string; name: string } | null>(null);

  // Load user's organization on mount
  useEffect(() => {
    if (session?.user?.id) {
      loadOrganization();
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (organizationId) {
      loadMembers();
      loadInvitations();
    }
    loadRoles();
  }, [organizationId]);

  const loadOrganization = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoadingOrganization(true);
      const data = await api.getUserOrganizations();

      if (data.organizations && data.organizations.length > 0) {
        // Use the first organization (in future, allow user to select)
        setOrganizationId(data.organizations[0].id);
      } else {
        toast.error('No organization found. Please contact support.');
      }
    } catch (error: any) {
      console.error('Failed to load organization', error);
      toast.error(error.message || 'Failed to load organization');
    } finally {
      setIsLoadingOrganization(false);
    }
  };

  const loadMembers = async () => {
    try {
      setIsLoadingMembers(true);
      const data = await api.getTeamMembers(organizationId);
      setMembers(data.members);
    } catch (error: any) {
      console.error('Failed to load team members', error);
      toast.error(error.message || 'Failed to load team members');
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const loadInvitations = async () => {
    try {
      setIsLoadingInvitations(true);
      const data = await api.getInvitations(organizationId);
      setInvitations(data.invitations);
    } catch (error: any) {
      console.error('Failed to load invitations', error);
      toast.error(error.message || 'Failed to load invitations');
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  const loadRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const data = await api.getRoles();
      // Count members for each role
      const rolesWithCounts = data.roles.map((role: Role) => ({
        ...role,
        memberCount: members.filter(m => m.role === role.id).length,
      }));
      setRoles(rolesWithCounts);
    } catch (error: any) {
      console.error('Failed to load roles', error);
      toast.error(error.message || 'Failed to load roles');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    if (!session?.user?.id) {
      toast.error('You must be logged in to send invitations');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setIsSendingInvite(true);
      await api.createInvitation({
        organizationId,
        email: inviteEmail,
        role: inviteRole,
        invitedBy: session.user.id,
      });

      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');

      // Reload invitations
      await loadInvitations();
    } catch (error: any) {
      console.error('Failed to send invitation', error);
      toast.error(error.message || 'Failed to send invitation');
    } finally {
      setIsSendingInvite(false);
    }
  };

  const openRemoveMemberDialog = (memberId: string, memberName: string) => {
    setMemberToRemove({ id: memberId, name: memberName });
    setRemoveMemberDialogOpen(true);
  };

  const handleRemoveMember = async () => {
    if (!session?.user?.id || !memberToRemove) return;

    try {
      await api.removeMember(memberToRemove.id);
      toast.success(`${memberToRemove.name} has been removed from the team`);

      // Update local state
      setMembers(members.filter(m => m.id !== memberToRemove.id));
      setRemoveMemberDialogOpen(false);
      setMemberToRemove(null);
    } catch (error: any) {
      console.error('Failed to remove member', error);
      toast.error(error.message || 'Failed to remove team member');
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: string, memberName: string) => {
    if (!session?.user?.id) return;

    try {
      await api.updateMemberRole(memberId, newRole);
      toast.success(`${memberName}'s role has been updated to ${newRole}`);

      // Update local state
      setMembers(members.map(m =>
        m.id === memberId ? { ...m, role: newRole as any } : m
      ));
    } catch (error: any) {
      console.error('Failed to update member role', error);
      toast.error(error.message || 'Failed to update member role');
    }
  };

  const handleResendInvitation = async (invitationId: string, email: string) => {
    try {
      await api.resendInvitation(invitationId);
      toast.success(`Invitation resent to ${email}`);

      // Reload invitations to get updated data
      await loadInvitations();
    } catch (error: any) {
      console.error('Failed to resend invitation', error);
      toast.error(error.message || 'Failed to resend invitation');
    }
  };

  const openRevokeDialog = (invitationId: string, email: string) => {
    setInvitationToRevoke({ id: invitationId, email });
    setRevokeDialogOpen(true);
  };

  const handleRevokeInvitation = async () => {
    if (!invitationToRevoke) return;

    try {
      await api.revokeInvitation(invitationToRevoke.id);
      toast.success(`Invitation for ${invitationToRevoke.email} has been revoked`);

      // Update local state
      setInvitations(invitations.filter(i => i.id !== invitationToRevoke.id));
      setRevokeDialogOpen(false);
      setInvitationToRevoke(null);
    } catch (error: any) {
      console.error('Failed to revoke invitation', error);
      toast.error(error.message || 'Failed to revoke invitation');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'admin':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'member':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'viewer':
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const filteredMembers = members.filter(member =>
    member.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b">
          <div className="flex items-center gap-2 px-3">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-6" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Team</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto flex items-center gap-2 px-3">
            <ModeToggle />
          </div>
        </header>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <RiTeamLine className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Team Management</h1>
                  <p className="text-muted-foreground">
                    Manage team members, roles, and invitations
                  </p>
                </div>
              </div>
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <RiUserAddLine className="h-4 w-4 mr-2" />
                    Invite Member
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Invite Team Member</DialogTitle>
                    <DialogDescription>
                      Send an invitation to join your team
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="colleague@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        disabled={isSendingInvite}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role">Role</Label>
                      <Select value={inviteRole} onValueChange={setInviteRole} disabled={isSendingInvite}>
                        <SelectTrigger id="role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setInviteDialogOpen(false)}
                      disabled={isSendingInvite}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleInvite} disabled={isSendingInvite}>
                      {isSendingInvite && <RiLoader2Line className="mr-2 h-4 w-4 animate-spin" />}
                      {isSendingInvite ? 'Sending...' : 'Send Invitation'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="members" className="space-y-6">
              <TabsList>
                <TabsTrigger value="members">
                  <RiTeamLine className="h-4 w-4 mr-2" />
                  Members
                </TabsTrigger>
                <TabsTrigger value="roles">
                  <RiShieldUserLine className="h-4 w-4 mr-2" />
                  Roles
                </TabsTrigger>
                <TabsTrigger value="invitations">
                  <RiMailSendLine className="h-4 w-4 mr-2" />
                  Invitations
                </TabsTrigger>
              </TabsList>

              {/* Members Tab */}
              <TabsContent value="members" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Team Members</CardTitle>
                        <CardDescription>
                          {isLoadingMembers ? 'Loading...' : `${members.length} member${members.length !== 1 ? 's' : ''} in your team`}
                        </CardDescription>
                      </div>
                      <div className="relative w-64">
                        <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search members..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingMembers ? (
                      <div className="flex items-center justify-center py-12">
                        <RiLoader2Line className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : filteredMembers.length === 0 ? (
                      <div className="text-center py-12">
                        <RiTeamLine className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="text-sm text-muted-foreground">
                          {searchQuery ? 'No members found matching your search' : 'No team members yet'}
                        </p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Member</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Joined</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredMembers.map((member, index) => (
                            <motion.tr
                              key={member.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className="group"
                            >
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <Avatar>
                                    <AvatarFallback>
                                      {member.userName.split(' ').map(n => n[0]).join('').toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="font-medium">{member.userName}</div>
                                    <div className="text-sm text-muted-foreground">
                                      {member.userEmail}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={getRoleBadgeColor(member.role)}>
                                  {member.role}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {new Date(member.joinedAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="text-right">
                                {member.role !== 'owner' && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon">
                                        <RiMoreLine className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => handleUpdateRole(member.id, 'admin', member.userName)}>
                                        <RiEditLine className="h-4 w-4 mr-2" />
                                        Make Admin
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleUpdateRole(member.id, 'member', member.userName)}>
                                        <RiEditLine className="h-4 w-4 mr-2" />
                                        Make Member
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        className="text-destructive"
                                        onClick={() => openRemoveMemberDialog(member.id, member.userName)}
                                      >
                                        <RiDeleteBinLine className="h-4 w-4 mr-2" />
                                        Remove Member
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </TableCell>
                            </motion.tr>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Roles Tab */}
              <TabsContent value="roles" className="space-y-4">
                {isLoadingRoles ? (
                  <div className="flex items-center justify-center py-12">
                    <RiLoader2Line className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {roles.map((role, index) => (
                      <motion.div
                        key={role.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10">
                                  <RiShieldUserLine className="w-4 h-4 text-primary" />
                                </div>
                                <CardTitle>{role.name}</CardTitle>
                              </div>
                              <Badge variant="secondary">
                                {role.memberCount || 0} member{role.memberCount !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            <CardDescription>{role.description}</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              <div className="text-sm font-medium">Permissions:</div>
                              <div className="flex flex-wrap gap-2">
                                {role.permissions.map((permission) => (
                                  <Badge
                                    key={permission}
                                    variant="outline"
                                    className="text-xs"
                                  >
                                    <RiCheckLine className="h-3 w-3 mr-1" />
                                    {permission.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Invitations Tab */}
              <TabsContent value="invitations" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Pending Invitations</CardTitle>
                    <CardDescription>
                      {isLoadingInvitations ? 'Loading...' : `${invitations.filter(i => i.status === 'pending').length} pending invitation${invitations.filter(i => i.status === 'pending').length !== 1 ? 's' : ''}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoadingInvitations ? (
                      <div className="flex items-center justify-center py-12">
                        <RiLoader2Line className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : invitations.length === 0 ? (
                      <div className="text-center py-12">
                        <RiMailSendLine className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="text-sm text-muted-foreground">No pending invitations</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Invite team members to get started
                        </p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Sent By</TableHead>
                            <TableHead>Expires</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invitations.map((invitation, index) => (
                            <motion.tr
                              key={invitation.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <TableCell className="font-medium">
                                {invitation.email}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {invitation.role}
                                </Badge>
                              </TableCell>
                              <TableCell>{invitation.invitedBy}</TableCell>
                              <TableCell>
                                {new Date(invitation.expiresAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={invitation.status === 'pending' ? 'default' : 'secondary'}
                                >
                                  {invitation.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleResendInvitation(invitation.id, invitation.email)}
                                  >
                                    <RiMailSendLine className="h-4 w-4 mr-2" />
                                    Resend
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => openRevokeDialog(invitation.id, invitation.email)}
                                  >
                                    <RiCloseLine className="h-4 w-4 mr-2" />
                                    Revoke
                                  </Button>
                                </div>
                              </TableCell>
                            </motion.tr>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </SidebarInset>

      {/* Remove Member Alert Dialog */}
      <AlertDialog open={removeMemberDialogOpen} onOpenChange={setRemoveMemberDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <span className="font-semibold text-foreground">
                {memberToRemove?.name}
              </span>
              {' '}from the team? They will immediately lose access to all team resources and data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Invitation Alert Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke the invitation for{' '}
              <span className="font-semibold text-foreground">
                {invitationToRevoke?.email}
              </span>
              ? This action cannot be undone. They will need to be invited again to join the team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeInvitation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}
